import { useCallback, useMemo, useState } from 'react';

const GOOGLE_GSI_SRC = 'https://accounts.google.com/gsi/client';
const GOOGLE_DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
const GOOGLE_TOKEN_STORAGE_KEY = 'rda_google_drive_oauth_token_v1';

const getGoogleClientId = () => {
  return String((typeof import.meta !== 'undefined' && import.meta?.env && import.meta.env.VITE_GOOGLE_CLIENT_ID) || '').trim();
};

const getGoogleApiOrigin = () => 'https://www.googleapis.com';

const loadGoogleIdentityScript = () => new Promise((resolve, reject) => {
  if (typeof window === 'undefined') {
    reject(new Error('Janela do navegador indisponível para carregar o Google Identity Services.'));
    return;
  }

  if (window.google?.accounts?.oauth2) {
    resolve(window.google);
    return;
  }

  const existing = document.querySelector(`script[src="${GOOGLE_GSI_SRC}"]`);
  if (existing) {
    existing.addEventListener('load', () => resolve(window.google), { once: true });
    existing.addEventListener('error', () => reject(new Error('Falha ao carregar o Google Identity Services.')), { once: true });
    return;
  }

  const script = document.createElement('script');
  script.src = GOOGLE_GSI_SRC;
  script.async = true;
  script.defer = true;
  script.onload = () => resolve(window.google);
  script.onerror = () => reject(new Error('Falha ao carregar o Google Identity Services.'));
  document.head.appendChild(script);
});

const readStoredToken = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(GOOGLE_TOKEN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const accessToken = String(parsed.access_token || '').trim();
    const expiresAt = Number(parsed.expires_at || 0);
    if (!accessToken) return null;
    if (expiresAt && Date.now() >= (expiresAt - 60_000)) {
      window.sessionStorage.removeItem(GOOGLE_TOKEN_STORAGE_KEY);
      return null;
    }
    return {
      accessToken,
      expiresAt,
    };
  } catch {
    return null;
  }
};

const persistToken = (tokenResponse) => {
  if (typeof window === 'undefined') return null;
  const accessToken = String(tokenResponse?.access_token || '').trim();
  const expiresInSeconds = Number(tokenResponse?.expires_in || 0);
  if (!accessToken) return null;
  const payload = {
    access_token: accessToken,
    expires_at: expiresInSeconds > 0 ? Date.now() + (expiresInSeconds * 1000) : 0,
  };
  window.sessionStorage.setItem(GOOGLE_TOKEN_STORAGE_KEY, JSON.stringify(payload));
  return {
    accessToken: payload.access_token,
    expiresAt: payload.expires_at,
  };
};

const getGoogleAccessToken = async () => {
  const cached = readStoredToken();
  if (cached?.accessToken) return cached.accessToken;

  const clientId = getGoogleClientId();
  if (!clientId) {
    throw new Error('VITE_GOOGLE_CLIENT_ID não configurado. Preencha essa variável no .env.local para ativar o upload OAuth pessoal do Google Drive.');
  }

  const google = await loadGoogleIdentityScript();
  if (!google?.accounts?.oauth2?.initTokenClient) {
    throw new Error('Google Identity Services indisponível no navegador.');
  }

  return await new Promise((resolve, reject) => {
    let settled = false;
    const tokenClient = google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: GOOGLE_DRIVE_SCOPE,
      callback: (response) => {
        if (settled) return;
        settled = true;
        const accessToken = String(response?.access_token || '').trim();
        if (!accessToken) {
          reject(new Error(String(response?.error || 'Falha ao obter Access Token do Google Drive.')));
          return;
        }
        const stored = persistToken(response);
        resolve(stored?.accessToken || accessToken);
      },
      error_callback: (error) => {
        if (settled) return;
        settled = true;
        reject(new Error(String(error?.message || error?.type || 'Falha ao autenticar com Google Drive via OAuth.')));
      },
    });
    tokenClient.requestAccessToken({ prompt: 'consent' });
  });
};

const buildMultipartBody = async ({ blob, fileName, mimeType, parentId }) => {
  const boundary = `rda-drive-${Math.random().toString(36).slice(2)}`;
  const metadata = {
    name: String(fileName || 'arquivo').trim() || 'arquivo',
    mimeType,
    parents: parentId ? [parentId] : undefined,
  };
  const encoder = new TextEncoder();
  const preamble =
    `--${boundary}\r\n` +
    'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
    `${JSON.stringify(metadata)}\r\n` +
    `--${boundary}\r\n` +
    `Content-Type: ${mimeType}\r\n\r\n`;
  const closing = `\r\n--${boundary}--`;
  const fileBuffer = await blob.arrayBuffer();
  const multipartBody = new Blob([
    encoder.encode(preamble),
    fileBuffer,
    encoder.encode(closing),
  ], { type: `multipart/related; boundary=${boundary}` });

  return {
    body: multipartBody,
    boundary,
  };
};

const normalizeDriveResponse = (driveData) => {
  const safe = driveData && typeof driveData === 'object' ? driveData : {};
  return {
    provider: 'gdrive',
    fileId: String(safe.id || safe.fileId || '').trim(),
    webViewLink: String(safe.webViewLink || '').trim(),
    webContentLink: String(safe.webContentLink || '').trim(),
  };
};

export function useDriveUpload() {
  const [uploading, setUploading] = useState(false);
  const [lastError, setLastError] = useState('');
  const [stage, setStage] = useState('idle');

  const googleClientId = useMemo(() => getGoogleClientId(), []);

  const uploadFile = useCallback(async ({ file, parentId, fileName } = {}) => {
    const blob = file instanceof File || file instanceof Blob ? file : null;
    if (!blob) {
      throw new Error('Arquivo inválido para upload no Google Drive.');
    }
    if (!googleClientId) {
      throw new Error('VITE_GOOGLE_CLIENT_ID não configurado. Preencha essa variável no .env.local para ativar o upload OAuth pessoal do Google Drive.');
    }

    const cleanName = String(fileName || blob?.name || 'arquivo').trim() || 'arquivo';
    const mimeType = String(blob?.type || 'application/octet-stream').trim() || 'application/octet-stream';
    const targetFolderId = String(parentId || '').trim();

    setUploading(true);
    setLastError('');
    setStage('auth');

    try {
      const accessToken = await getGoogleAccessToken();
      if (!accessToken) {
        throw new Error('Token OAuth do Google Drive ausente após autenticação.');
      }

      setStage('upload');
      const { body, boundary } = await buildMultipartBody({
        blob,
        fileName: cleanName,
        mimeType,
        parentId: targetFolderId,
      });

      const uploadUrl = `${getGoogleApiOrigin()}/upload/drive/v3/files?uploadType=multipart&supportsAllDrives=true&fields=id,webViewLink,webContentLink`;
      const uploadResp = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': `multipart/related; boundary=${boundary}`,
        },
        body,
      });

      const driveData = await uploadResp.json().catch(() => ({}));
      if (!uploadResp.ok) {
        const detail = String(driveData?.error?.message || driveData?.detail || uploadResp.statusText || 'Falha no upload OAuth para o Google Drive.').trim();
        throw new Error(detail || 'Falha no upload OAuth para o Google Drive.');
      }

      const provisionalStorageEngine = normalizeDriveResponse(driveData);
      if (!provisionalStorageEngine.fileId) {
        throw new Error('Google Drive não retornou o fileId após o upload.');
      }

      setStage('done');

      return {
        ok: true,
        storageEngineData: provisionalStorageEngine,
        raw: driveData,
      };
    } catch (error) {
      const msg = String(error?.message || error || 'Falha no upload para o Google Drive.');
      setLastError(msg);
      setStage('error');
      throw new Error(msg);
    } finally {
      setUploading(false);
    }
  }, [googleClientId]);

  return {
    uploadFile,
    uploading,
    lastError,
    stage,
    googleClientId,
  };
}
